import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { C } from '../lib/theme';

function useClinicPatients(clinicId) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    const { data } = await supabase
      .from('profiles')
      .select('id,name,weight_current,weight_goal,drug,clinic_confirmed')
      .eq('clinic_id', clinicId)
      .eq('role', 'patient');
    setPatients(data || []);
    setLoading(false);
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  return { patients, loading, reload: load };
}

function ReissueAccess({ patientId }) {
  const [confirming, setConfirming] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [issuedCode, setIssuedCode] = useState(null);

  async function reissue() {
    setLoading(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/clinic/codes/reissue', {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
        body: JSON.stringify({ patientProfileId: patientId }),
      });
      const data = await res.json();
      if (!res.ok) { setError('No se pudo generar el código'); return; }
      setIssuedCode(data.code.code);
      setConfirming(false);
    } catch {
      setError('No se pudo conectar con el servidor');
    } finally {
      setLoading(false);
    }
  }

  if (issuedCode) {
    return (
      <div style={{ background: C.mintLt, borderRadius: 10, padding: '0.9rem', textAlign: 'center' }}>
        <div style={{ fontSize: '0.78rem', color: C.carbon, marginBottom: '0.4rem' }}>Nuevo código de acceso — dáselo al paciente:</div>
        <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: 700, color: C.sage, letterSpacing: '0.05em' }}>{issuedCode}</div>
      </div>
    );
  }

  if (confirming) {
    return (
      <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 10, padding: '0.9rem' }}>
        <p style={{ margin: '0 0 0.7rem', fontSize: '0.82rem', color: C.carbon, lineHeight: 1.5 }}>
          Solo genera esto si el paciente confirma que perdió su acceso — este código sustituirá su sesión anterior.
        </p>
        {error && <p style={{ color: C.terra, fontSize: '0.8rem', marginBottom: '0.6rem' }}>{error}</p>}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setConfirming(false)} style={{ flex: 1, padding: '0.5rem', borderRadius: 8, border: `1px solid ${C.mint}`, background: '#fff', color: C.gray, fontSize: '0.8rem', cursor: 'pointer' }}>
            Cancelar
          </button>
          <button onClick={reissue} disabled={loading} style={{ flex: 1, padding: '0.5rem', borderRadius: 8, border: 'none', background: '#B45309', color: '#fff', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}>
            {loading ? 'Generando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={() => setConfirming(true)} style={{ width: '100%', padding: '0.7rem', borderRadius: 10, border: `1.5px solid ${C.mint}`, background: '#fff', color: C.sage, fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
      Reenviar acceso
    </button>
  );
}

function TabPatients({ clinicId }) {
  const { patients, loading } = useClinicPatients(clinicId);
  const [selected, setSelected] = useState(null);

  if (loading) {
    return <p style={{ color: C.gray, fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>Cargando pacientes...</p>;
  }

  if (selected) {
    const p = patients.find(pt => pt.id === selected);
    if (!p) { setSelected(null); return null; }
    return (
      <div>
        <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: C.sage, fontWeight: 700, cursor: 'pointer', padding: '0.5rem 0', marginBottom: '1rem', fontSize: '0.95rem' }}>
          ← Volver a pacientes
        </button>
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
          <h3 style={{ margin: '0 0 1rem', fontFamily: 'Georgia, serif', color: C.carbon }}>{p.name || 'Sin nombre'}</h3>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Peso actual', val: p.weight_current ? `${p.weight_current} kg` : '—' },
              { label: 'Objetivo', val: p.weight_goal ? `${p.weight_goal} kg` : '—' },
              { label: 'Medicamento', val: p.drug || '—' },
              { label: 'Vinculación', val: p.clinic_confirmed ? '✓ Confirmado' : '⏳ Pendiente' },
            ].map(s => (
              <div key={s.label} style={{ flex: '1 1 120px', background: C.cream, borderRadius: 10, padding: '0.6rem', textAlign: 'center' }}>
                <div style={{ fontWeight: 700, color: C.sage }}>{s.val}</div>
                <div style={{ fontSize: '0.75rem', color: C.gray }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)' }}>
          <ReissueAccess patientId={p.id} />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ background: `linear-gradient(135deg, ${C.carbon}, #2D3748)`, borderRadius: 16, padding: '1.2rem', color: '#fff', marginBottom: '1rem' }}>
        <h2 style={{ margin: '0 0 1rem', fontFamily: 'Georgia, serif' }}>Panel de Clínica</h2>
        <div style={{ display: 'flex', gap: '0.8rem' }}>
          <div style={{ flex: 1, background: 'rgba(255,255,255,0.12)', borderRadius: 10, padding: '0.6rem', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{patients.length}</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.8 }}>Pacientes</div>
          </div>
        </div>
      </div>

      <h3 style={{ margin: '0 0 0.8rem', color: C.carbon, fontFamily: 'Georgia, serif' }}>Todos los pacientes</h3>
      {patients.length === 0 && <p style={{ color: C.gray, fontSize: '0.85rem', fontStyle: 'italic' }}>Aún no tienes pacientes vinculados.</p>}
      {patients.map(p => (
        <div key={p.id} onClick={() => setSelected(p.id)} style={{
          background: '#fff', borderRadius: 12, padding: '0.9rem 1.1rem',
          boxShadow: '0 2px 8px rgba(44,106,79,0.07)', marginBottom: '0.6rem',
          display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer',
        }}>
          <div style={{ width: 40, height: 40, borderRadius: '50%', background: C.mintLt, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, color: C.sage, flexShrink: 0 }}>
            {(p.name || '??').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, color: C.carbon, fontSize: '0.95rem' }}>{p.name || 'Sin nombre'}</div>
            <div style={{ fontSize: '0.78rem', color: C.gray }}>{p.drug || 'Sin medicación registrada'}</div>
          </div>
          <span style={{ fontSize: '0.75rem', fontWeight: 700, color: p.clinic_confirmed ? C.sage : C.gray }}>
            {p.clinic_confirmed ? '✓ Confirmado' : '⏳ Pendiente'}
          </span>
        </div>
      ))}
    </div>
  );
}

function TabAnalytics({ clinicId }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    async function load() {
      if (!clinicId) return;
      const { data: patients } = await supabase
        .from('profiles')
        .select('id')
        .eq('clinic_id', clinicId)
        .eq('role', 'patient');
      const ids = (patients || []).map(p => p.id);
      if (!ids.length) { setCounts({ weightLogs: 0, chatMessages: 0, sportClicks: 0 }); return; }

      const { data: sportVideos } = await supabase.from('content_items').select('id').eq('type', 'sport_video');
      const svIds = (sportVideos || []).map(v => v.id);

      const [{ count: weightLogs }, { count: chatMessages }, sportResult] = await Promise.all([
        supabase.from('weight_logs').select('id', { count: 'exact', head: true }).in('user_id', ids),
        supabase.from('chat_messages').select('id', { count: 'exact', head: true }).in('user_id', ids),
        svIds.length
          ? supabase.from('content_item_clicks').select('id', { count: 'exact', head: true }).eq('clinic_id', clinicId).in('content_item_id', svIds)
          : Promise.resolve({ count: 0 }),
      ]);
      setCounts({ weightLogs: weightLogs || 0, chatMessages: chatMessages || 0, sportClicks: sportResult.count || 0 });
    }
    load();
  }, [clinicId]);

  if (!counts) {
    return <p style={{ color: C.gray, fontSize: '0.9rem', textAlign: 'center', padding: '2rem 0' }}>Cargando analítica...</p>;
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
      {[
        { label: 'Registros de peso', val: counts.weightLogs },
        { label: 'Interacciones de chat', val: counts.chatMessages },
        { label: 'Vídeos de deporte reproducidos', val: counts.sportClicks },
      ].map(m => (
        <div key={m.label} style={{ background: '#fff', borderRadius: 14, padding: '1rem', boxShadow: '0 2px 8px rgba(44,106,79,0.07)', textAlign: 'center' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '1.8rem', fontWeight: 700, color: C.sage }}>{m.val}</div>
          <div style={{ fontSize: '0.78rem', color: C.carbon, fontWeight: 600, marginTop: '0.2rem' }}>{m.label}</div>
        </div>
      ))}
    </div>
  );
}

const CODE_STATUS_LABELS = {
  unused: { label: 'Sin usar', color: C.sage, bg: C.mintLt },
  reserved: { label: 'Pendiente confirmación', color: '#92400E', bg: '#FEF3C7' },
  redeemed: { label: 'Usado', color: C.gray, bg: '#F3F4F6' },
};

function TabCodes({ clinicId }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!clinicId) return;
    setLoading(true);
    const { data } = await supabase
      .from('clinic_codes')
      .select('id,code,status,created_at')
      .eq('clinic_id', clinicId)
      .order('created_at', { ascending: false });
    setCodes(data || []);
    setLoading(false);
  }, [clinicId]);

  useEffect(() => { load(); }, [load]);

  async function generateCode() {
    setGenerating(true);
    setError('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch('/api/clinic/codes/generate', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      });
      const data = await res.json();
      if (!res.ok) { setError('No se pudo generar el código'); return; }
      setCodes(prev => [data.code, ...prev]);
    } catch {
      setError('No se pudo conectar con el servidor');
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div>
      <div style={{ background: '#fff', borderRadius: 16, padding: '1.2rem', boxShadow: '0 2px 12px rgba(44,106,79,0.08)', marginBottom: '1rem' }}>
        <h3 style={{ margin: '0 0 0.8rem', fontFamily: 'Georgia, serif', color: C.carbon }}>Códigos para pacientes</h3>
        <p style={{ color: C.gray, fontSize: '0.85rem', lineHeight: 1.5, margin: '0 0 1rem' }}>
          Genera un código de un solo uso y entrégaselo a tu paciente. Lo introducirá al registrarse en Vera.
        </p>
        <button onClick={generateCode} disabled={generating} style={{ padding: '0.7rem 1.2rem', borderRadius: 10, border: 'none', background: C.sage, color: '#fff', fontWeight: 700, cursor: 'pointer', opacity: generating ? 0.6 : 1 }}>
          {generating ? 'Generando...' : '+ Generar código nuevo'}
        </button>
        {error && <p style={{ color: C.terra, fontSize: '0.82rem', marginTop: '0.6rem' }}>{error}</p>}
      </div>

      {loading ? (
        <p style={{ color: C.gray, fontSize: '0.9rem', textAlign: 'center', padding: '1rem 0' }}>Cargando códigos...</p>
      ) : codes.length === 0 ? (
        <p style={{ color: C.gray, fontSize: '0.85rem', fontStyle: 'italic' }}>Todavía no has generado ningún código.</p>
      ) : (
        codes.map(c => {
          const info = CODE_STATUS_LABELS[c.status] || CODE_STATUS_LABELS.unused;
          return (
            <div key={c.id} style={{ background: '#fff', borderRadius: 12, padding: '0.8rem 1rem', boxShadow: '0 2px 8px rgba(44,106,79,0.07)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <div style={{ flex: 1, fontFamily: 'monospace', fontSize: '1rem', fontWeight: 700, color: C.carbon, letterSpacing: '0.05em' }}>{c.code}</div>
              <div style={{ background: info.bg, color: info.color, borderRadius: 20, padding: '0.3rem 0.8rem', fontSize: '0.75rem', fontWeight: 700 }}>{info.label}</div>
            </div>
          );
        })
      )}
    </div>
  );
}

const TABS = [
  { id: 'patients', label: 'Pacientes', icon: '👥' },
  { id: 'analytics', label: 'Analítica', icon: '📊' },
  { id: 'codes', label: 'Códigos', icon: '🔑' },
];

export default function ClinicDashboard({ profile, onSignOut }) {
  const [activeTab, setActiveTab] = useState('patients');
  const clinicId = profile?.clinic_id;

  return (
    <div style={{ maxWidth: 480, margin: '0 auto', fontFamily: 'sans-serif', background: C.cream, minHeight: '100vh', paddingBottom: 70 }}>
      <div style={{ padding: '1rem' }}>
        {activeTab === 'patients' && <TabPatients clinicId={clinicId} />}
        {activeTab === 'analytics' && <TabAnalytics clinicId={clinicId} />}
        {activeTab === 'codes' && <TabCodes clinicId={clinicId} />}
      </div>
      <nav style={{ position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: 480, background: '#fff', borderTop: '1px solid #E5E7EB', display: 'flex', zIndex: 100 }}>
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ flex: 1, padding: '0.6rem 0', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem', color: activeTab === tab.id ? C.sage : C.gray, fontSize: '1.2rem' }}>
            <span>{tab.icon}</span>
            <span style={{ fontSize: '0.65rem', fontWeight: activeTab === tab.id ? 700 : 400 }}>{tab.label}</span>
          </button>
        ))}
        <button onClick={onSignOut} style={{ flex: 1, padding: '0.6rem 0', border: 'none', background: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.1rem', color: C.gray, fontSize: '1.2rem' }}>
          <span>🚪</span>
          <span style={{ fontSize: '0.65rem' }}>Salir</span>
        </button>
      </nav>
    </div>
  );
}
